import { Route } from 'react-router-dom'
import {
  LBTeamsIcon,
  LBLayoutIcon,
  LBHomeIcon,
  LBMultipleUsersIcon,
  LBMultipleAccountsIcon,
  LBGraphStatsIcon,
  LBListToDoIcon,
  LBGitActivityIcon,
  LBUserIcon,
  LBSlackIcon,
  LBLabIcon
} from '@linearb/buzz'
import { Organizations } from './views/Organizations'
import LoginPage from './views/Login'
import Oauth from './views/Oauth'
import LBSymbolsIcon from './custom-components/LBSymbolsIcon'
import GitstreamIcon from './custom-components/GitstreamIcon'
import { isGitStreamAvailable } from './utils/featureFlags'
import { IS_PROD_EU } from './constants/apis'
import { lazy } from 'react'

// Every page except the landing route (Organizations) and the auth pages is
// split out, so a page's code is only fetched when it is first visited.
// App.tsx holds the single Suspense boundary these resolve against.
const FeatureRequests = lazy(() => import('./views/FeatureRequests'))
const GitOrgSearch = lazy(() => import('./views/GitOrgSearch'))
const UserOrgSearch = lazy(() => import('./views/UserOrgSearch'))
const OrganizationDetails = lazy(() => import('./views/OrganizationDetails'))
const Projects = lazy(() => import('./views/Projects'))
const MultiOrgAccounts = lazy(() => import('./views/MultiOrgAccounts'))
const MultiOrgDetails = lazy(
  () => import('./views/MultiOrgAccounts/MultiOrgDetails')
)
const OPAs = lazy(() => import('./views/OnPremiseApp/OnPremiseAppsPage'))
const OnBoarding = lazy(
  () => import('./views/Organizations/OnBoarding/OnBoardingPage')
)
const BackofficeUsers = lazy(() => import('./views/BackofficeUsers'))
const TroubleshootingPage = lazy(
  () => import('./views/Troubleshooting/TroubleshootingPage')
)
const GitstreamTroubleshootingPage = lazy(
  () => import('./views/Troubleshooting/GitstreamTroubleshootingPage')
)
const ChangelogPage = lazy(() => import('./views/Changelog/ChangelogPage'))
const McpConsent = lazy(() => import('./views/McpConsent'))
const ConnectAi = lazy(() => import('./views/ConnectAi'))
const SlackCommandsPage = lazy(
  () => import('./views/SlackCommands/SlackCommandsPage')
)
const FeatureFlags = lazy(() => import('./views/FeatureFlags'))
const UsagePage = lazy(() => import('./views/Usage/UsagePage'))

interface AppRoute {
  type?: string
  name: string
  key: string
  route: string
  icon?: React.ReactNode
  visible?: boolean
  // Unregistered on prod-eu: backed by US-only sources, or managed in US
  hiddenOnEu?: boolean
  component: React.ReactElement
}

const ALL_AUTHENTICATED_ROUTES: AppRoute[] = [
  {
    type: 'collapse',
    name: 'Organizations',
    key: 'organization',
    icon: <LBTeamsIcon width={16} height={16} />,
    route: '/organization',
    component: <Organizations />
  },
  {
    visible: false,
    type: 'collapse',
    name: 'Organization',
    key: 'organization-details',
    route: '/organization/:id',
    component: <OrganizationDetails />
  },
  {
    type: 'collapse',
    name: 'Multi-Org',
    key: 'multi-org',
    icon: <LBMultipleAccountsIcon width={16} height={16} />,
    route: '/multi-org',
    component: <MultiOrgAccounts />
  },
  {
    visible: false,
    type: 'collapse',
    name: 'Multi-Org Details',
    key: 'multi-org-details',
    route: '/multi-org/:id',
    component: <MultiOrgDetails />
  },
  {
    type: 'collapse',
    name: 'Feature Requests',
    key: 'feature-requests',
    hiddenOnEu: true,
    icon: <LBListToDoIcon width={16} height={16} />,
    route: '/feature-requests',
    component: <FeatureRequests />
  },
  {
    type: 'collapse',
    name: 'Git Org Search',
    key: 'git-org-search',
    icon: <LBGitActivityIcon width={16} height={16} />,
    route: '/git-org-search',
    component: <GitOrgSearch />
  },
  {
    type: 'collapse',
    name: 'User Org Search',
    key: 'user-org-search',
    icon: <LBUserIcon width={16} height={16} />,
    route: '/user-org-search',
    component: <UserOrgSearch />
  },
  {
    type: 'collapse',
    name: 'Projects',
    key: 'projects',
    icon: <LBLayoutIcon width={16} height={16} />,
    route: '/projects',
    component: <Projects />
  },
  {
    type: 'collapse',
    name: 'OPA',
    key: 'OPAs',
    icon: <LBHomeIcon width={16} height={16} />,
    route: '/opas',
    component: <OPAs />
  },
  {
    visible: false,
    type: 'collapse',
    name: 'On Boarding',
    key: 'onboarding',
    route: '/onboarding',
    component: <OnBoarding />
  },
  {
    visible: false,
    type: 'collapse',
    name: 'Backoffice Users',
    key: 'backoffice-users',
    icon: <LBMultipleUsersIcon width={16} height={16} />,
    route: '/backoffice-users',
    component: <BackofficeUsers />
  },
  {
    type: 'collapse',
    name: 'Slack Commands',
    key: 'slack-commands',
    icon: <LBSlackIcon width={16} height={16} />,
    route: '/slack-commands',
    component: <SlackCommandsPage />
  },
  {
    type: 'collapse',
    name: 'Feature Flags',
    key: 'feature-flags',
    icon: <LBLabIcon width={16} height={16} />,
    route: '/feature-flags',
    component: <FeatureFlags />
  },
  {
    type: 'collapse',
    name: 'WorkerB',
    key: 'workerb',
    icon: <LBSymbolsIcon fontSize='small' />,
    route: '/workerb',
    component: <TroubleshootingPage />
  },
  {
    type: 'collapse',
    name: 'gitStream',
    key: 'gitstream',
    icon: <GitstreamIcon width={20} height={20} />,
    route: '/gitstream',
    component: <GitstreamTroubleshootingPage />,
    visible: isGitStreamAvailable()
  },
  {
    type: 'collapse',
    name: 'Usage',
    key: 'usage',
    // Reads US Datadog (RUM is off on prod-eu)
    hiddenOnEu: true,
    icon: <LBGraphStatsIcon width={16} height={16} />,
    route: '/usage',
    component: <UsagePage />
  },
  {
    visible: false,
    type: 'collapse',
    name: 'Changelog',
    key: 'changelog',
    route: '/changelog',
    component: <ChangelogPage />
  },
  {
    visible: false,
    type: 'collapse',
    name: 'Connect AI Assistant',
    key: 'connect-ai',
    hiddenOnEu: true,
    route: '/connect-ai',
    component: <ConnectAi />
  }
]

export const AUTHENTICATED_ROUTES = ALL_AUTHENTICATED_ROUTES.filter(
  route => !(IS_PROD_EU && route.hiddenOnEu)
)

export const AUTH_CALLBACK_ROUTE: AppRoute = {
  name: 'OAuth Callback',
  key: 'auth-callback',
  route: '/callback/backoffice_google_identity',
  component: <Oauth />
}

export const MCP_CONSENT_ROUTE: AppRoute = {
  name: 'MCP Consent',
  key: 'mcp-consent',
  route: '/mcp/consent',
  component: <McpConsent />
}

export const LOGIN_ROUTE: AppRoute = {
  name: 'Login',
  key: 'login',
  route: '/login',
  component: <LoginPage />
}

const ALL_ROUTES: AppRoute[] = [
  ...AUTHENTICATED_ROUTES,
  MCP_CONSENT_ROUTE,
  LOGIN_ROUTE,
  AUTH_CALLBACK_ROUTE
]

export default ALL_ROUTES.map(route => {
  return <Route path={route.route} element={route.component} key={route.key} />
})
